/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 22/04/24
 */

import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { Workplan, WorkplanAttachment } from '@/wpt-lib/interfaces/Workplan';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import FileUpload from '@/components/FileUpload';
import { useGetRequest, usePostRequest } from '@/utils/useStatefulRequests';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { LoadingSpinner } from '@/components';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';

interface SupportingDocumentsTileProps {
    currentAssignment: Assignment;
    workplan: Workplan | undefined;
    loading: boolean;
    baseApiUrl: string;
    isReadonly: boolean;
    version?: number;
}

const SupportingDocumentsTile: React.FC<SupportingDocumentsTileProps> = ({
    currentAssignment,
    workplan,
    version,
    loading,
    baseApiUrl,
    isReadonly,
}: SupportingDocumentsTileProps) => {
    const helpText =
        'Attach any supporting documents you wish your supervisor to consider as part of your end of year workplan review. Attachments MUST be in PDF format and not exceed 10MB.\n' +
        'Note: Do not attach any confidential information (e.g. health information or passport information).';

    const {
        makeRequest: getWorkplanAttachments,
        error: getAttachmentsError,
        loading: attachmentsLoading,
        responseData: workplanAttachments,
    } = useGetRequest<WorkplanAttachment[]>();

    const {
        makeRequest: uploadFiles,
        error: uploadError,
        loading: uploadLoading,
        responseData: uploadedAttachments,
    } = usePostRequest<WorkplanAttachment[], FormData>();

    const [uploadedFiles, setUploadedFiles] = useState<WorkplanAttachment[]>([]);

    const getListOfWorkplanAttachments = useCallback(async () => {
        try {
            await getWorkplanAttachments(`/${baseApiUrl}/${version ?? workplan?.currentVersion?.version}/files`, {
                params: {
                    assignment: currentAssignment?.id,
                },
            });
        } catch (error) {
            //handled by hook
        }
    }, [baseApiUrl, currentAssignment, version]);

    const uploadWorkplanAttachments = useCallback(
        async (acceptedFiles: File[]) => {
            try {
                const formData = new FormData();
                acceptedFiles.forEach((file) => {
                    formData.append('files', file);
                });

                await uploadFiles(`/${baseApiUrl}/files`, formData, {
                    params: { assignment: currentAssignment?.id },
                });
                setUploadedFiles((prevFiles) => [...prevFiles, ...(uploadedAttachments ?? [])]);
                await getListOfWorkplanAttachments();
            } catch (error) {
                //handled by hook
            }
        },
        [currentAssignment, getListOfWorkplanAttachments],
    );

    useAsyncEffect(async () => {
        if (currentAssignment) {
            await getListOfWorkplanAttachments();
        }
    }, [currentAssignment, getListOfWorkplanAttachments]);

    if (loading || attachmentsLoading) {
        return <LoadingSpinner />;
    }

    if (getAttachmentsError) {
        return (
            <SimpleAlert variant={'left-accent'}>
                <Text>Cannot get list of attachments, please refresh the page and try again</Text>
            </SimpleAlert>
        );
    }

    const allFiles = [...uploadedFiles, ...(workplanAttachments || [])];

    return (
        <Box>
            <Flex alignItems="center" mb={3}>
                <Heading as="h3" size={'sm'}>
                    Supporting Documents
                    <InfoTooltip tooltipLabel={'Supporting Documents Tooltip'} tooltipInfoText={helpText} />
                </Heading>
            </Flex>
            {!!uploadError && (
                <SimpleAlert variant={'left-accent'} mb={3}>
                    <Text>Could not upload the file(s), please refresh the page and try again</Text>
                </SimpleAlert>
            )}
            <FileUpload
                onDrop={(acceptedFiles: File[]) => {
                    uploadWorkplanAttachments(acceptedFiles);
                }}
                currentAssignment={currentAssignment}
                baseApiUrl={baseApiUrl}
                getListOfWorkplanAttachments={getListOfWorkplanAttachments}
                isReadonly={isReadonly}
                existingFiles={allFiles}
                uploadLoading={uploadLoading}
            />
        </Box>
    );
};

export default SupportingDocumentsTile;
